/**
 * Model Registry Schemas
 *
 * Unified model configuration system that combines:
 * - Provider configurations (API keys, endpoints)
 * - Model definitions (all models from all providers)
 * - UI metadata (settings, descriptions, defaults)
 * - Model-to-provider mapping
 *
 * Single source of truth for AI model configurations.
 */

import { z } from 'zod';
import { AIProviderSchema, AINodeTypeSchema, NodeModelConfigSchema } from '../../canvas/node-models';

// =============================================================================
// Provider Configuration
// =============================================================================

/**
 * Provider status
 */
export const ProviderStatusSchema = z.enum(['active', 'inactive']);
export type ProviderStatus = z.infer<typeof ProviderStatusSchema>;

/**
 * Model type supported by providers
 */
export const ModelTypeSchema = z.enum(['text-to-text', 'text-to-image', 'text-to-video']);
export type ModelType = z.infer<typeof ModelTypeSchema>;

/**
 * Provider-specific model configuration
 */
export const ProviderModelConfigSchema = z.object({
  type: ModelTypeSchema,
  systemPrompt: z.string().optional(),
  parameters: z.record(z.string(), z.any()),
});

export type ProviderModelConfig = z.infer<typeof ProviderModelConfigSchema>;

/**
 * Provider configuration
 */
export const ProviderConfigSchema = z.object({
  id: AIProviderSchema,
  name: z.string(),
  status: ProviderStatusSchema,
  apiKey: z.string().optional(), // Can contain "{{ENV:KEY_NAME}}" placeholders
  endpoint: z.string().optional(),
  models: z.record(z.string(), ProviderModelConfigSchema), // model ID -> config
});

export type ProviderConfig = z.infer<typeof ProviderConfigSchema>;

// =============================================================================
// Unified Model Registry
// =============================================================================

/**
 * Complete model entry with provider config + UI metadata
 */
export const ModelRegistryEntrySchema = NodeModelConfigSchema.extend({
  nodeType: AINodeTypeSchema,
});

export type ModelRegistryEntry = z.infer<typeof ModelRegistryEntrySchema>;

/**
 * Complete model registry configuration
 */
export const ModelRegistrySchema = z.object({
  version: z.string(), // Config version (e.g., "1.0.0")
  providers: z.record(z.string(), ProviderConfigSchema), // provider ID -> config
  models: z.array(ModelRegistryEntrySchema), // All models with UI metadata
  modelToProvider: z.record(z.string(), z.string()), // model ID -> provider ID
  fallbackStrategy: z.record(z.string(), z.string().nullable()), // model ID -> fallback model ID
  userSelectableModels: z.array(z.string()), // model IDs that users can select
});

export type ModelRegistry = z.infer<typeof ModelRegistrySchema>;

// =============================================================================
// API Response Types
// =============================================================================

/**
 * Models filtered by node type (for API responses)
 */
export const NodeTypeModelsResponseSchema = z.object({
  models: z.array(NodeModelConfigSchema),
});

export type NodeTypeModelsResponse = z.infer<typeof NodeTypeModelsResponseSchema>;

/**
 * Full registry response (admin only)
 */
export const FullRegistryResponseSchema = ModelRegistrySchema;
export type FullRegistryResponse = z.infer<typeof FullRegistryResponseSchema>;
